import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LocationProvider } from './contexts/LocationContext';
import { BusinessTypeProvider } from './contexts/BusinessTypeContext';
import AuthPage from './pages/AuthPage';
import LandingPage from './pages/LandingPage';
import DemoLogin from './pages/DemoLogin';
import LocationSelection from './pages/LocationSelection';
import DashboardLayout from './components/Layout/DashboardLayout';
import Dashboard from './pages/Dashboard';
import DataEntry from './pages/DataEntry';
import FeedLogs from './pages/FeedLogs';
import MedicineLogs from './pages/MedicineLogs';
import Reports from './pages/Reports';
import ComplianceReport from './pages/reports/ComplianceReport';
import FeedTrends from './pages/reports/FeedTrends';
import MedicineTrends from './pages/reports/MedicineTrends';
import MortalityReport from './pages/reports/MortalityReport';
import InventorySnapshot from './pages/reports/InventorySnapshot';
import CycleCount from './pages/reports/CycleCount';
import FinancialReports from './pages/reports/FinancialReports';
import RevenueProjections from './pages/reports/RevenueProjections';
import CostAnalysis from './pages/reports/CostAnalysis';
import CyclePlanning from './pages/CyclePlanning';
import WaterQualityLogs from './pages/WaterQualityLogs';
import FishSeedLogs from './pages/FishSeedLogs';
import FishSamplingLogs from './pages/FishSamplingLogs';
import DeadFishLogs from './pages/DeadFishLogs';
import Inventory from './pages/Inventory';
import AdminPanel from './pages/AdminPanel';
import LoadingSpinner from './components/UI/LoadingSpinner';
// Goat pages
import GoatMaster from './pages/goat/GoatMaster';
import GoatMasterForm from './pages/goat/forms/GoatMasterForm';
import GoatSheds from './pages/goat/GoatSheds';
import GoatShedForm from './pages/goat/forms/GoatShedForm';
import GoatLogs from './pages/goat/GoatLogs';
import GoatBreedingLogs from './pages/goat/GoatBreedingLogs';
import GoatBreedingForm from './pages/goat/forms/GoatBreedingForm';
import GoatKiddingLogs from './pages/goat/GoatKiddingLogs';
import GoatKiddingForm from './pages/goat/forms/GoatKiddingForm';
import GoatHealthLogs from './pages/goat/GoatHealthLogs';
import GoatHealthForm from './pages/goat/forms/GoatHealthForm';
import GoatVaccinationForm from './pages/goat/forms/GoatVaccinationForm';
import VaccinationRecommendations from './pages/goat/VaccinationRecommendations';
import GoatReports from './pages/goat/GoatReports';
import GoatDashboard from './pages/goat/GoatDashboard';
import GoatDataEntry from './pages/goat/GoatDataEntry';
import GoatWeightRegister from './pages/goat/GoatWeightRegister';
import GoatMilkRegister from './pages/goat/GoatMilkRegister';
import GoatBreedingPlan from './pages/goat/GoatBreedingPlan';
import GoatSaleRegister from './pages/goat/GoatSaleRegister';
import GoatFeedLogs from './pages/goat/GoatFeedLogs';
import GoatMedicineLogs from './pages/goat/GoatMedicineLogs';
import GoatIncomeLogs from './pages/goat/GoatIncomeLogs';
import GoatExpenseLogs from './pages/goat/GoatExpenseLogs';
import GoatFinancialReports from './pages/goat/GoatFinancialReports';
import GoatHealthComplianceReportPage from './pages/goat/GoatHealthComplianceReport';
import GoatBreedingPerformanceReportPage from './pages/goat/GoatBreedingPerformanceReport';
import GoatKiddingProductivityReportPage from './pages/goat/GoatKiddingProductivityReport';
import GoatPopulationReportPage from './pages/goat/GoatPopulationReport';
import GoatGrowthReportPage from './pages/goat/GoatGrowthReport';
import GoatMortalityReportPage from './pages/goat/GoatMortalityReport';
import PaymentRequests from './pages/PaymentRequests';
import Payroll from './pages/Payroll';
import EmployeeManagement from './pages/payroll/EmployeeManagement';
import AttendancePage from './pages/payroll/Attendance';
import LeaveManagement from './pages/payroll/LeaveManagement';
import SalaryGenerationPage from './pages/payroll/SalaryGeneration';
import AdvancePaymentPage from './pages/payroll/AdvancePayment';
import PaymentRequestForm from './pages/PaymentRequestForm';
import RetailSales from './pages/RetailSales';
import OnFarmSaleForm from './pages/retailsales/OnFarmSaleForm';
import MarketSaleForm from './pages/retailsales/MarketSaleForm';
import SellerManagement from './pages/retailsales/SellerManagement';
import SellerForm from './pages/retailsales/SellerForm';
import RetailSalesList from './pages/retailsales/RetailSalesList';
import RetailSaleDetail from './pages/retailsales/RetailSaleDetail';
import LiveSalesMonitor from './pages/retailsales/LiveSalesMonitor';
import AssetList from './pages/assets/AssetList';
import AssetForm from './pages/assets/AssetForm';
import Tasks from './pages/Tasks';
import TaskDetail from './pages/TaskDetail';
import TaskForm from './pages/TaskForm';
import TaskTemplates from './pages/TaskTemplates';
import { ToastProvider } from './contexts/ToastContext';
import { initAnalytics } from './analytics';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// Protected Route wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

// Public Route wrapper
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner />
      </div>
    );
  }

  if (user) {
    // Always send authenticated users into the app shell; inner layout will handle location state
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

function AppContent() {
  useEffect(() => {
    initAnalytics();
  }, []);

  return (
    <Router>
      <Routes>
        <Route
          path="/"
          element={
            <PublicRoute>
              <LandingPage />
            </PublicRoute>
          }
        />
        <Route
          path="/auth"
          element={
            <PublicRoute>
              <AuthPage />
            </PublicRoute>
          }
        />
        <Route
          path="/demo"
          element={
            <PublicRoute>
              <DemoLogin />
            </PublicRoute>
          }
        />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <LocationProvider>
                <BusinessTypeProvider>
                  <Routes>
                  <Route path="locations" element={<LocationSelection />} />
                  <Route path="/*" element={<DashboardLayout />}>
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="data-entry/*" element={<DataEntry />} />
                    <Route path="retail-sales" element={<RetailSales />} />
                    <Route path="retail-sales/on-farm" element={<OnFarmSaleForm />} />
                    <Route path="retail-sales/market" element={<MarketSaleForm />} />
                    <Route path="retail-sales/list" element={<RetailSalesList />} />
                    <Route path="retail-sales/live" element={<LiveSalesMonitor />} />
                    <Route path="retail-sales/:id" element={<RetailSaleDetail />} />
                    <Route path="retail-sales/sellers" element={<SellerManagement />} />
                    <Route path="retail-sales/sellers/new" element={<SellerForm />} />
                    <Route path="retail-sales/sellers/:id/edit" element={<SellerForm />} />
                    <Route path="feed-logs" element={<FeedLogs />} />
                    <Route path="medicine-logs" element={<MedicineLogs />} />
                    <Route path="water-quality-logs" element={<WaterQualityLogs />} />
                    <Route path="fish-seed-logs" element={<FishSeedLogs />} />
                    <Route path="fish-sampling-logs" element={<FishSamplingLogs />} />
                    <Route path="dead-fish-logs" element={<DeadFishLogs />} />
                    <Route path="assets" element={<AssetList />} />
                    <Route path="assets/new" element={<AssetForm />} />
                    <Route path="assets/:id/edit" element={<AssetForm />} />
                    <Route path="inventory" element={<Inventory />} />
                    <Route path="reports" element={<Reports />} />
                    <Route path="reports/compliance" element={<ComplianceReport />} />
                    <Route path="reports/feed-trends" element={<FeedTrends />} />
                    <Route path="reports/medicine-trends" element={<MedicineTrends />} />
                    <Route path="reports/mortality" element={<MortalityReport />} />
                    <Route path="reports/inventory-snapshot" element={<InventorySnapshot />} />
                    <Route path="reports/cycle-count" element={<CycleCount />} />
                    <Route path="reports/financials" element={<FinancialReports />} />
                    <Route path="reports/revenue-projections" element={<RevenueProjections />} />
                    <Route path="reports/cost-analysis" element={<CostAnalysis />} />
                    <Route path="cycle-planning" element={<CyclePlanning />} />
                    {/* Goat routes */}
                    <Route path="goat/dashboard" element={<GoatDashboard />} />
                    <Route path="goat/data-entry/*" element={<GoatDataEntry />} />
                    <Route path="goat/master" element={<GoatMaster />} />
                    <Route path="goat/master/new" element={<GoatMasterForm />} />
                    <Route path="goat/master/:id/edit" element={<GoatMasterForm />} />
                    <Route path="goat/sheds" element={<GoatSheds />} />
                    <Route path="goat/sheds/new" element={<GoatShedForm />} />
                    <Route path="goat/sheds/:id/edit" element={<GoatShedForm />} />
                    <Route path="goat/logs" element={<GoatLogs />} />
                    <Route path="goat/breeding-logs" element={<GoatBreedingLogs />} />
                    <Route path="goat/breeding/new" element={<GoatBreedingForm />} />
                    <Route path="goat/kidding-logs" element={<GoatKiddingLogs />} />
                    <Route path="goat/kidding/new" element={<GoatKiddingForm />} />
                    <Route path="goat/health-logs" element={<GoatHealthLogs />} />
                    <Route path="goat/health-logs/recommendations" element={<VaccinationRecommendations />} />
                    <Route path="goat/health-logs/vaccination/new" element={<GoatVaccinationForm />} />
                    <Route path="goat/health/new" element={<GoatHealthForm />} />
                    <Route path="goat/weight-register" element={<GoatWeightRegister />} />
                    <Route path="goat/milk-register" element={<GoatMilkRegister />} />
                    <Route path="goat/breeding-plan" element={<GoatBreedingPlan />} />
                    <Route path="goat/sale-register" element={<GoatSaleRegister />} />
                    <Route path="goat/feed-logs" element={<GoatFeedLogs />} />
                    <Route path="goat/medicine-logs" element={<GoatMedicineLogs />} />
                    <Route path="goat/income-logs" element={<GoatIncomeLogs />} />
                    <Route path="goat/expense-logs" element={<GoatExpenseLogs />} />
                    <Route path="goat/reports" element={<GoatReports />} />
                    <Route path="goat/report/financials" element={<GoatFinancialReports />} />
                    <Route path="goat/report/health-compliance" element={<GoatHealthComplianceReportPage />} />
                    <Route path="goat/report/breeding-performance" element={<GoatBreedingPerformanceReportPage />} />
                    <Route path="goat/report/kidding-productivity" element={<GoatKiddingProductivityReportPage />} />
                    <Route path="goat/report/population" element={<GoatPopulationReportPage />} />
                    <Route path="goat/report/growth" element={<GoatGrowthReportPage />} />
                    <Route path="goat/report/mortality" element={<GoatMortalityReportPage />} />
                    <Route path="payment-requests" element={<PaymentRequests />} />
                    <Route path="payment-requests/new" element={<PaymentRequestForm />} />
                    <Route path="payment-requests/:id/edit" element={<PaymentRequestForm />} />
                    <Route path="payroll" element={<Payroll />} />
                    <Route path="payroll/employees" element={<EmployeeManagement />} />
                    <Route path="payroll/attendance" element={<AttendancePage />} />
                    <Route path="payroll/leave" element={<LeaveManagement />} />
                    <Route path="payroll/salary" element={<SalaryGenerationPage />} />
                    <Route path="payroll/advance-payment" element={<AdvancePaymentPage />} />
                    <Route path="tasks" element={<Tasks />} />
                    <Route path="tasks/new" element={<TaskForm />} />
                    <Route path="tasks/:taskId" element={<TaskDetail />} />
                    <Route path="tasks/:taskId/edit" element={<TaskForm />} />
                    <Route path="tasks/templates" element={<TaskTemplates />} />
                    <Route path="admin/*" element={<AdminPanel />} />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Route>
                </Routes>
                </BusinessTypeProvider>
              </LocationProvider>
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/locations" replace />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;